/* eslint-disable indent */
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { Coins, HelpCircle, Scroll, Sparkles, Sword } from "lucide-react";
import Head from "next/head";
import EntityManagement from "@/components/molecules/EntityManagement";
import LootGeneratorForm from "@/components/molecules/LootGeneratorForm";
import LootView from "@/components/molecules/LootView";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { deleteGenerations, editLoot, listGenerations } from "@/lib/actions";
import { EditLootGenerationRequest, LootGenerationResultDTO } from "@/types";
const LootGeneratorPage = () => {
  const [loots, setLoots] = useState<LootGenerationResultDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    fetchLoots();
  }, []);
  const fetchLoots = async () => {
    setIsLoading(true);
    try {
      const response = await listGenerations("LOOT");
      if (response.error) {
        console.error("Error fetching loots:", response.error);
      } else {
        const parsedLoots = (response.data || []).map((loot: {
          outputData: string;
        }) => ({
          ...loot,
          outputData: typeof loot.outputData === "string" ? JSON.parse(loot.outputData) : loot.outputData
        }));
        setLoots(parsedLoots);
      }
    } catch (error) {
      console.error("Error fetching loots:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleGenerate = (loot: LootGenerationResultDTO) => {
    setLoots(prev => [loot, ...prev]);
  };
  const handleDelete = async (ids: string[]) => {
    try {
      await deleteGenerations(ids);
      setLoots(prev => prev.filter(loot => !ids.includes(loot.id)));
    } catch (error) {
      console.error("Error deleting loots:", error);
    }
  };
  const handleUpdate = async (id: string, data: EditLootGenerationRequest) => {
    try {
      const response = await editLoot(id, data);
      if (response.response) {
        setLoots(prev => prev.map(loot => loot.id === id ? {
          ...loot,
          outputData: {
            lootName: response.response?.lootName || data.lootName,
            narrative: response.response?.narrative || data.narrative,
            lootItems: response.response?.lootItems || data.lootItems
          }
        } : loot));
        toast.success("Loot collection updated successfully!");
      }
    } catch (error) {
      console.error("Error updating loot:", error);
      toast.error("Failed to update loot collection");
      throw error;
    }
  };
  return <>
            <Head data-sentry-element="Head" data-sentry-source-file="index.tsx">
                <title>
                    CharGen: Fantasy Loot Generator | Easily Create Fantasy D&D
                    Loot
                </title>
                <meta name="description" content="Generate balanced and thematic loot in seconds for D&D 5E, Pathfinder, and other RPGs. Create unique treasure hoards, magical items, and rewards with our AI-powered loot generator. Perfect for DMs and GMs." data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:title" content="CharGen: Fantasy Loot Generator | Easily Create Fantasy D&D Loot" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:description" content="Generate balanced and thematic loot in seconds for D&D 5E, Pathfinder, and other RPGs. Create unique treasure hoards, magical items, and rewards with our AI-powered loot generator. Perfect for DMs and GMs." data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:image" key="og:image" content="https://charsheet-gen.s3.eu-west-2.amazonaws.com/OGFantasyLootGenerator.png" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:image:url" key="og:image:url" content="https://charsheet-gen.s3.eu-west-2.amazonaws.com/OGFantasyLootGenerator.png" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:type" content="website" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:url" key="og:url" content="https://char-gen.com/loot-generator" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <link rel="canonical" href="https://char-gen.com/loot-generator" />
                <meta property="og:image:width" key="og:image:width" content="1200" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:image:height" key="og:image:height" content="630" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:image:alt" key="og:image:alt" content="Fantasy Loot Generator" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="og:image:type" key="og:image:type" content="image/png" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="twitter:card" content="summary_large_image" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="twitter:title" key="twitter:title" content="CharGen: The Best Fantasy Loot Generator" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="twitter:description" key="twitter:description" content="Generate balanced and thematic loot in seconds for D&D 5E, Pathfinder, and other RPGs. Create unique treasure hoards, magical items, and rewards with our AI-powered loot generator. Perfect for DMs and GMs." data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <meta property="twitter:image" key="twitter:image" content="https://charsheet-gen.s3.eu-west-2.amazonaws.com/OGFantasyLootGenerator.png" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "CharGen",
          url: "https://char-gen.com/loot-generator",
          description: "Generate balanced and thematic loot in seconds for D&D 5E, Pathfinder, and other RPGs. Create unique treasure hoards, magical items, and rewards with our AI-powered loot generator. Perfect for DMs and GMs."
        })
      }} />
            </Head>
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 py-12 px-4 overflow-x-none">
                {/* Hero Section */}
                <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="max-w-6xl mx-auto px-4 mb-12 text-center" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Badge className="mb-4 bg-slate-500/20 text-slate-400 hover:bg-slate-500/30" data-sentry-element="Badge" data-sentry-source-file="index.tsx">
                        <Sparkles className="w-4 h-4 mr-2" data-sentry-element="Sparkles" data-sentry-source-file="index.tsx" /> New AI-Powered
                        Features
                    </Badge>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-400 to-emerald-600 bg-clip-text text-transparent mb-4">
                        Fantasy RPG Loot Generator
                    </h1>
                    <p className="text-lg text-slate-200 max-w-2xl mx-auto">
                        Create balanced and thematic loot collections for your
                        tabletop RPG adventures. Perfect for Dungeon Masters and
                        Game Masters running D&D 5E, Pathfinder, and other
                        systems.
                    </p>
                    <div className="flex flex-wrap justify-center gap-6 mt-8">
                        <div className="flex items-center gap-2 text-emerald-400">
                            <Coins className="w-5 h-5" data-sentry-element="Coins" data-sentry-source-file="index.tsx" />
                            <span>Level-Appropriate Rewards</span>
                        </div>
                        <div className="flex items-center gap-2 text-emerald-400">
                            <Sword className="w-5 h-5" data-sentry-element="Sword" data-sentry-source-file="index.tsx" />
                            <span>Thematic Collections</span>
                        </div>
                        <div className="flex items-center gap-2 text-emerald-400">
                            <Scroll className="w-5 h-5" data-sentry-element="Scroll" data-sentry-source-file="index.tsx" />
                            <span>Rich Descriptions</span>
                        </div>
                    </div>
                </motion.div>

                {/* Free Features Banner */}
                <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="max-w-6xl mx-auto px-4 mb-8" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <div className="bg-gradient-to-r from-emerald-500/20 via-emerald-500/10 to-emerald-500/20 rounded-lg border border-emerald-500/30 p-4 sm:p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-5 h-5 text-emerald-400" data-sentry-element="Sparkles" data-sentry-source-file="index.tsx" />
                            <h3 className="text-lg font-semibold text-emerald-400">
                                Generate Taverns, Shops & Loot For Free!
                            </h3>
                        </div>
                        <p className="text-slate-200 mb-4">
                            Create unlimited content for your D&D campaigns with
                            our AI-powered generators. Free sign-up required.
                            Premium subscribers can edit their creations.
                            <span className="block text-xs text-slate-400 mt-1">
                                *Obviously don&apos;t go spamming like 1,000,000
                                loot generations in a minute, but you get the
                                point.
                            </span>
                        </p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                    <Sparkles className="w-4 h-4 text-emerald-400" data-sentry-element="Sparkles" data-sentry-source-file="index.tsx" />
                                </div>
                                <p className="text-slate-200">
                                    <span className="text-emerald-400 font-semibold">
                                        Free
                                    </span>{" "}
                                    RPG loot generation
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                    <Sword className="w-4 h-4 text-emerald-400" data-sentry-element="Sword" data-sentry-source-file="index.tsx" />
                                </div>
                                <p className="text-slate-200">
                                    <span className="text-emerald-400 font-semibold">
                                        All
                                    </span>{" "}
                                    RPG Generators included
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                    <Scroll className="w-4 h-4 text-emerald-400" data-sentry-element="Scroll" data-sentry-source-file="index.tsx" />
                                </div>
                                <p className="text-slate-200">
                                    <span className="text-emerald-400 font-semibold">
                                        No
                                    </span>{" "}
                                    generation limits
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Main Form */}
                <LootGeneratorForm onGenerate={handleGenerate} data-sentry-element="LootGeneratorForm" data-sentry-source-file="index.tsx" />

                {/* Loot Management */}
                <div id="generated-loot" className="max-w-6xl mx-auto px-4 mt-16 pt-16 border-t border-slate-800">
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-slate-100">
                            Your Generated Loot
                        </h2>
                        <p className="text-slate-300 mt-2">
                            Manage and browse your collection of generated
                            treasure hoards and rewards
                        </p>
                    </div>

                    <EntityManagement entities={loots} isLoading={isLoading} variant="loot" onDelete={handleDelete} onUpdate={handleUpdate} EntityView={LootView} data-sentry-element="EntityManagement" data-sentry-source-file="index.tsx" />
                </div>

                {/* Rewards Banner
                 <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="max-w-6xl mx-auto px-4 mb-8"
                 >
                    <div className="bg-gradient-to-r from-sky-500/20 via-sky-500/10 to-sky-500/20 rounded-lg border border-emerald-500/30 p-4 sm:p-6">
                        <h3 className="text-lg font-semibold text-emerald-400 mb-2">
                            🎁 Daily Rewards & Bonuses
                        </h3>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                    <Coins className="w-4 h-4 text-emerald-400" />
                                </div>
                                <p className="text-slate-200">
                                    Get{" "}
                                    <span className="text-emerald-400 font-semibold">
                                        10 free gold
                                    </span>{" "}
                                    daily just by logging in!
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                    <Users className="w-4 h-4 text-emerald-400" />
                                </div>
                                {state.isLoggedIn ? (
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <button className="flex items-center group cursor-pointer text-left">
                                                <span className="text-slate-200 group-hover:text-slate-100">
                                                    Earn{" "}
                                                    <span className="text-emerald-400 font-semibold group-hover:text-emerald-300">
                                                        100 gold
                                                    </span>{" "}
                                                    for each friend referral
                                                </span>
                                                <QuestionMarkCircledIcon className="w-4 h-4 ml-1 text-slate-400 group-hover:text-slate-300 shrink-0" />
                                            </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-80">
                                            <div className="space-y-4">
                                                <h4 className="font-medium text-slate-200">
                                                    Your Referral Link
                                                </h4>
                                                <div className="flex items-center gap-2">
                                                    <code className="flex-1 p-2 rounded bg-slate-800 text-sm text-slate-200">
                                                        https://char-gen.com?referralCode=
                                                        {
                                                            state.user
                                                                ?.referralCode
                                                        }
                                                    </code>
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(
                                                                `https://char-gen.com?referralCode=${state.user?.referralCode}`
                                                            );
                                                            toast.success(
                                                                "Referral link copied!"
                                                            );
                                                        }}
                                                    >
                                                        <Copy className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                                <p className="text-sm text-slate-400">
                                                    Share this link with
                                                    friends. You&apos;ll receive
                                                    100 gold when they sign up!
                                                </p>
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                ) : (
                                    <p className="text-slate-200">
                                        Earn{" "}
                                        <span className="text-emerald-400 font-semibold">
                                            100 gold
                                        </span>{" "}
                                        for each friend referral
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                    <CalendarDays className="w-4 h-4 text-emerald-400" />
                                </div>
                                <p className="text-slate-200">
                                    Bonus gold for login streaks!
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                    <Sparkles className="w-4 h-4 text-emerald-400" />
                                </div>
                                <p className="text-slate-200">
                                    Sign up now for{" "}
                                    <span className="text-emerald-400 font-semibold">
                                        10 gold
                                    </span>{" "}
                                    welcome bonus!
                                </p>
                            </div>
                        </div>
                    </div>
                 </motion.div> */}

                {/* Features Section */}
                <div className="max-w-6xl mx-auto px-4 mt-16">
                    <h2 className="text-2xl font-semibold text-slate-200 mb-6">
                        Features
                    </h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-slate-900/40 backdrop-blur-sm p-6 rounded-xl border border-slate-700">
                            <h3 className="text-lg font-medium text-slate-100 mb-2">
                                Balanced Rewards
                            </h3>
                            <p className="text-slate-300">
                                Generate level-appropriate loot that matches
                                your party&apos;s capabilities and the
                                challenges they&apos;ve overcome.
                            </p>
                        </div>
                        <div className="bg-slate-900/40 backdrop-blur-sm p-6 rounded-xl border border-slate-700">
                            <h3 className="text-lg font-medium text-slate-100 mb-2">
                                Thematic Collections
                            </h3>
                            <p className="text-slate-300">
                                Create themed loot collections from dragon
                                hoards to ancient ruins treasures, each with its
                                own unique narrative.
                            </p>
                        </div>
                        <div className="bg-slate-900/40 backdrop-blur-sm p-6 rounded-xl border border-slate-700">
                            <h3 className="text-lg font-medium text-slate-100 mb-2">
                                Detailed Items
                            </h3>
                            <p className="text-slate-300">
                                Each item comes with rich descriptions,
                                appropriate market values, and special
                                characteristics that make them memorable.
                            </p>
                        </div>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="max-w-6xl mx-auto px-4 mt-16">
                    <div className="flex items-center gap-2 mb-6">
                        <HelpCircle className="w-6 h-6 text-slate-400" data-sentry-element="HelpCircle" data-sentry-source-file="index.tsx" />
                        <h2 className="text-2xl font-semibold text-slate-200">
                            Frequently Asked Questions
                        </h2>
                    </div>
                    <Accordion type="single" collapsible className="w-full" data-sentry-element="Accordion" data-sentry-source-file="index.tsx">
                        <AccordionItem value="what-is" data-sentry-element="AccordionItem" data-sentry-source-file="index.tsx">
                            <AccordionTrigger className="text-slate-100" data-sentry-element="AccordionTrigger" data-sentry-source-file="index.tsx">
                                What is the RPG Loot Generator?
                            </AccordionTrigger>
                            <AccordionContent className="text-slate-300" data-sentry-element="AccordionContent" data-sentry-source-file="index.tsx">
                                The RPG Loot Generator is a tool designed for
                                Dungeon Masters and Game Masters to create
                                balanced and thematic loot collections for their
                                tabletop roleplaying games. It generates
                                appropriate rewards based on party level and
                                defeated monsters, suitable for D&D 5E,
                                Pathfinder, and other fantasy RPG systems.
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="how-works" data-sentry-element="AccordionItem" data-sentry-source-file="index.tsx">
                            <AccordionTrigger className="text-slate-100" data-sentry-element="AccordionTrigger" data-sentry-source-file="index.tsx">
                                How does the generator work?
                            </AccordionTrigger>
                            <AccordionContent className="text-slate-300" data-sentry-element="AccordionContent" data-sentry-source-file="index.tsx">
                                Simply input your party&apos;s level, select a
                                theme, and optionally add details about defeated
                                monsters. The generator will create a balanced
                                collection of loot with appropriate items,
                                values, and descriptions, all tailored to your
                                party&apos;s level and the selected game system.
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="loot-types" data-sentry-element="AccordionItem" data-sentry-source-file="index.tsx">
                            <AccordionTrigger className="text-slate-100" data-sentry-element="AccordionTrigger" data-sentry-source-file="index.tsx">
                                What types of loot can I generate?
                            </AccordionTrigger>
                            <AccordionContent className="text-slate-300" data-sentry-element="AccordionContent" data-sentry-source-file="index.tsx">
                                You can generate various types of loot
                                collections including dungeon treasures,
                                dragon&apos;s hoards, noble&apos;s caches,
                                ancient ruins artifacts, magical treasuries, and
                                more. Each type comes with thematically
                                appropriate items and descriptions.
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="game-systems" data-sentry-element="AccordionItem" data-sentry-source-file="index.tsx">
                            <AccordionTrigger className="text-slate-100" data-sentry-element="AccordionTrigger" data-sentry-source-file="index.tsx">
                                Which game systems are supported?
                            </AccordionTrigger>
                            <AccordionContent className="text-slate-300" data-sentry-element="AccordionContent" data-sentry-source-file="index.tsx">
                                The generator primarily supports D&D 5E and
                                Pathfinder, but it can be used with any fantasy
                                RPG system. The items, values, and descriptions
                                are designed to be easily adaptable to your
                                preferred system.
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>

                {/* SEO Content */}
                <div className="max-w-6xl mx-auto px-4 mt-16">
                    <article className="prose prose-slate prose-invert max-w-none">
                        <h2>Create Balanced Rewards for Your RPG Campaign</h2>
                        <p>
                            Whether you&apos;re running a D&D 5E campaign, a
                            Pathfinder adventure, or any other fantasy RPG, our
                            loot generator helps you create balanced and
                            thematic rewards for your players. Each generated
                            collection comes complete with:
                        </p>
                        <ul>
                            <li>Level-appropriate items and treasure</li>
                            <li>Thematically consistent collections</li>
                            <li>Detailed item descriptions</li>
                            <li>Accurate market values</li>
                            <li>Rich narrative context</li>
                            <li>Monster-specific rewards</li>
                        </ul>
                        <p>
                            Perfect for both planned encounters and improvised
                            rewards, our generator helps Dungeon Masters and
                            Game Masters create memorable loot that enhances
                            their storytelling and maintains game balance.
                        </p>
                    </article>
                </div>
            </div>
        </>;
};
export default LootGeneratorPage;